import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Box, BoxProps } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import * as Tabs from '@givebutter/primitives/tabs';
import { useToast } from '@givebutter/primitives/toast';
import {
    faEnvelope,
    faPalette,
    faSave,
    faSms,
    faSpinnerThird,
} from '@fortawesome/pro-regular-svg-icons';
import {
    faArrowLeft,
    faEllipsisV,
    faEnvelopeOpenText,
    faEnvelope as faEnvelopeSolid,
    faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    CreateEngageMessageParams,
    deleteAccountMessageTemplate,
    fetchAdvancedEmailTemplateById,
    getAccountEngageMessageTemplates,
    getEngageMessageTemplates,
} from 'api/engage';
import { AdvancedTemplateCategories } from 'assets/js/pages/engage/components/AdvancedTemplateCategories';
import { EmailMessageTemplatePreview } from 'assets/js/pages/engage/components/EmailMessageTemplatePreview';
import {
    getAdvancedTemplateScratchTemplate,
    getAdvancedTemplateWithFooter,
} from 'assets/js/pages/engage/components/constants';
import { useAdvancedTemplateCategorySelection } from 'assets/js/pages/engage/useAdvancedTemplateCategorySelection';
import * as Dialog from 'components/dialog';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Input from 'components/input';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteAdvancedEmailTemplates } from 'customHooks/useInfiniteAdvancedEmailTemplates';
import { useIsPending } from 'customHooks/useIsPending';
import { isoDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { twJoin, twMerge } from 'tailwind-merge';
import {
    AdvancedMessageTemplateDetail,
    EngageMessageChannel,
    EngageMessageEditor,
    EngageMessageTemplateVisibility,
} from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface MessageTemplateDialogProps extends Dialog.DialogProps {
    channel: EngageMessageChannel;
    visibility?: EngageMessageTemplateVisibility;
    onTemplateSelection: (data: CreateEngageMessageParams) => void;
}

const MessageTemplateDialog = ({
    children,
    channel,
    visibility = 'engage',
    onTemplateSelection,
    ...rest
}: MessageTemplateDialogProps) => {
    const history = useHistory();
    const { accountId, hasGivebutterPlusAccess } = useCurrentAccount();
    const { success, error } = useToast();
    const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTab, setSelectedTab] = useState(
        channel === 'email' && hasGivebutterPlusAccess ? 'visual' : 'classic',
    );
    const { selectedCategories } = useAdvancedTemplateCategorySelection();

    const { data: givebutterTemplates } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessageTemplatesWithFilters({
            channel,
            visibility,
        }),
        queryFn: () => getEngageMessageTemplates({ channel: channel, visibility }),
        select: data => data.data,
    });

    const { data: accountTemplates } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageAccountMessageTemplatesWithFilters(accountId, channel),
        queryFn: () => getAccountEngageMessageTemplates(accountId, channel),
        select: data => data.data,
    });
    const hasSavedTemplates = accountTemplates.length > 0;

    const debouncedSearch = useDebounce(searchTerm);
    const debouncedCategory = useDebounce(selectedCategories);
    const [isAdvancedPending, advancedParams] = useIsPending(
        { search: debouncedSearch, category: debouncedCategory },
        250,
    );
    const { ref: advancedInfiniteRef, data: advancedTemplatesData } =
        useInfiniteAdvancedEmailTemplates(advancedParams);
    const advancedTemplates = useMemo(
        () => advancedTemplatesData.pages || [],
        [advancedTemplatesData],
    );

    const { mutate: deleteAccountTemplate } = useMutation<
        unknown,
        GivebutterAxiosError,
        { templateId: number }
    >({
        mutationFn: ({ templateId }) => deleteAccountMessageTemplate(accountId, templateId),
        onSuccess: () => {
            success('Template successfully deleted!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageAccountMessageTemplates(accountId),
            });
        },
        onError: e => {
            error(e.response?.data.message || 'Failed to delete template. Please try again');
        },
    });

    const { mutate: getAdvancedTemplate, isPending: isFetchingAdvancedTemplate } = useMutation<
        AdvancedMessageTemplateDetail,
        GivebutterAxiosError,
        { templateId: string }
    >({
        mutationFn: ({ templateId }) => fetchAdvancedEmailTemplateById(templateId),
        onSuccess: data => {
            onTemplateSelection({
                channel: 'email',
                editor: 'advanced',
                body: '',
                body_json: JSON.stringify(getAdvancedTemplateWithFooter(data.json_data)),
            });
        },
        onError: e => {
            error(e.response?.data.message || 'Failed to get template details.');
        },
    });

    const handleClickStartFromScratch = useCallback(() => {
        const editor: EngageMessageEditor =
            selectedTab === 'classic' || !hasGivebutterPlusAccess ? 'basic' : 'advanced';
        const body_json =
            selectedTab !== 'classic'
                ? JSON.stringify(getAdvancedTemplateScratchTemplate())
                : undefined;
        onTemplateSelection({
            channel,
            editor,
            body_json,
        });
    }, [channel, selectedTab, hasGivebutterPlusAccess, onTemplateSelection]);

    useEffect(() => {
        setSelectedTab(channel === 'email' ? 'visual' : 'classic');
    }, [channel]);

    return (
        <Dialog.Root {...rest}>
            <Dialog.Trigger asChild>{children}</Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content className="relative h-[800px] w-full max-w-5xl p-0">
                    <Tabs.Root
                        value={selectedTab}
                        onValueChange={setSelectedTab}
                        className={previewTemplateId ? 'hidden' : undefined}
                    >
                        <div
                            className={twJoin(
                                'flex min-w-full flex-col justify-between gap-4 bg-gray-100 p-4',
                                'sm:absolute sm:bottom-0 sm:left-0 sm:top-0 sm:min-w-48',
                            )}
                        >
                            <Tabs.List className="flex w-full flex-col gap-1">
                                <p className="font-medium">Templates</p>
                                <div className="flex flex-col gap-1">
                                    <TabsTrigger value="classic">
                                        <FontAwesomeIcon
                                            icon={channel === 'email' ? faEnvelope : faSms}
                                            className="group-data-[state=active]:text-blue-500"
                                        />
                                        {channel === 'email' ? 'Classic' : 'Library'}
                                    </TabsTrigger>
                                    {channel === 'email' && (
                                        <TabsTrigger
                                            value="visual"
                                            className={twMerge(
                                                hasGivebutterPlusAccess && '-order-1',
                                            )}
                                        >
                                            <FontAwesomeIcon
                                                icon={faPalette}
                                                className="group-data-[state=active]:text-blue-500"
                                            />
                                            Visual
                                        </TabsTrigger>
                                    )}
                                    <TabsTrigger value="saved">
                                        <FontAwesomeIcon
                                            icon={faSave}
                                            className="group-data-[state=active]:text-blue-500"
                                        />
                                        Saved
                                    </TabsTrigger>
                                </div>
                            </Tabs.List>
                            <Button
                                size="sm"
                                variant="outline"
                                color="neutral"
                                className="sticky bottom-4"
                                onClick={handleClickStartFromScratch}
                            >
                                Start from scratch
                            </Button>
                        </div>

                        <Tabs.Content
                            value="classic"
                            className="absolute inset-0 top-56 bg-white sm:left-48 sm:top-0"
                        >
                            <div className="flex h-full flex-col p-4 pt-12">
                                <div className="relative grid flex-grow-0 grid-cols-1 gap-6 overflow-auto sm:grid-cols-2 md:grid-cols-3">
                                    <StartFromScratchCard
                                        channel={channel}
                                        onSelect={handleClickStartFromScratch}
                                        className="h-52"
                                    />

                                    {givebutterTemplates.map(template => (
                                        <div
                                            key={template.id}
                                            className="group relative flex flex-col gap-2"
                                        >
                                            <Box className="min-h-52 overflow-hidden">
                                                {template.preview_image && (
                                                    <img
                                                        src={template.preview_image}
                                                        alt={template.name}
                                                        className="object-cover"
                                                    />
                                                )}
                                            </Box>
                                            <p className="font-medium">{template.name}</p>
                                            <div
                                                className={twMerge(
                                                    'absolute inset-0 bottom-8 flex flex-col justify-center gap-2 rounded-xl bg-gray-900 bg-opacity-50 p-4 opacity-0',
                                                    'group-hover:animate-opacity group-focus-within:animate-opacity group-focus-within:opacity-100 group-hover:opacity-100',
                                                )}
                                            >
                                                <Button
                                                    size="sm"
                                                    variant="solid"
                                                    color="primary"
                                                    onClick={() => {
                                                        onTemplateSelection({
                                                            channel: 'email',
                                                            message_template_id: template.id,
                                                            subject: template.subject,
                                                            body: template.body,
                                                            body_json: template.body_json,
                                                            internal_name: template.name,
                                                            editor: template.editor,
                                                        });
                                                    }}
                                                >
                                                    Use template
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Tabs.Content>
                        <Tabs.Content
                            value="visual"
                            className="absolute inset-0 top-56 bg-white sm:left-48 sm:top-0"
                        >
                            <div className="flex h-full flex-col gap-4 p-4">
                                <div className="flex items-center gap-2 pr-8">
                                    <Input.Root size={'sm'}>
                                        <Input.SearchIcon />
                                        <Input.Form
                                            placeholder="Search templates"
                                            value={searchTerm}
                                            onChange={e => setSearchTerm(e.currentTarget.value)}
                                        />
                                    </Input.Root>
                                    <AdvancedTemplateCategories />

                                    {(isAdvancedPending || isFetchingAdvancedTemplate) && (
                                        <div className="relative ml-auto">
                                            <Loader />
                                        </div>
                                    )}
                                </div>
                                <div
                                    className={twMerge(
                                        'relative grid flex-grow-0 grid-cols-1 gap-6 overflow-auto sm:grid-cols-2 md:grid-cols-3',
                                        isAdvancedPending && 'pointer-events-none opacity-50',
                                    )}
                                >
                                    {!searchTerm && selectedCategories.length === 0 && (
                                        <StartFromScratchCard
                                            channel={channel}
                                            onSelect={handleClickStartFromScratch}
                                            className="max-h-[516px]"
                                        />
                                    )}
                                    {advancedTemplates.map(template => (
                                        <div
                                            key={template.id}
                                            className="group flex flex-col gap-2"
                                        >
                                            <div className="relative">
                                                <Box className="relative h-[516px] overflow-hidden">
                                                    {template.preview_image && (
                                                        <img
                                                            src={template.preview_image}
                                                            alt={template.name}
                                                            className={twMerge(
                                                                'absolute left-0 right-0 top-0 min-h-80 w-full rounded-sm',
                                                            )}
                                                        />
                                                    )}
                                                </Box>

                                                <div
                                                    className={twMerge(
                                                        'absolute inset-0 flex flex-col justify-center gap-2 rounded-xl bg-gray-900 bg-opacity-50 p-4 opacity-0',
                                                        'group-hover:animate-opacity group-focus-within:animate-opacity group-focus-within:opacity-100 group-hover:opacity-100',
                                                    )}
                                                >
                                                    <Button
                                                        size="sm"
                                                        variant="solid"
                                                        color="primary"
                                                        disabled={isFetchingAdvancedTemplate}
                                                        onClick={() => {
                                                            if (hasGivebutterPlusAccess) {
                                                                getAdvancedTemplate({
                                                                    templateId: template.id,
                                                                });
                                                            } else {
                                                                history.push(
                                                                    `/accounts/${accountId}/settings/about/subscription`,
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        {isFetchingAdvancedTemplate && (
                                                            <FontAwesomeIcon
                                                                icon={faSpinnerThird}
                                                                className="animate-spin"
                                                            />
                                                        )}
                                                        {hasGivebutterPlusAccess
                                                            ? 'Use template'
                                                            : 'Upgrade to use'}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        color="neutral"
                                                        onClick={() =>
                                                            setPreviewTemplateId(template.id)
                                                        }
                                                    >
                                                        Preview
                                                    </Button>
                                                </div>
                                            </div>
                                            <p className="font-medium">{template.name}</p>
                                        </div>
                                    ))}
                                    <div ref={advancedInfiniteRef} />
                                </div>
                                {advancedTemplates.length <= 0 &&
                                    (!!searchTerm || selectedCategories.length > 0) && (
                                        <div className="col-span-3 flex min-h-96 flex-grow flex-col items-center justify-center gap-4">
                                            <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-gray-100 shadow-sm">
                                                <FontAwesomeIcon
                                                    icon={faPalette}
                                                    className="text-brand-violet-primary"
                                                />
                                            </div>
                                            <div className="flex flex-col items-center gap-1">
                                                <p className="font-medium">No templates found</p>
                                                <p className="text-sm">
                                                    Please adjust your search term or categories.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                            </div>
                        </Tabs.Content>
                        <Tabs.Content
                            value="saved"
                            className="absolute inset-0 top-56 bg-white sm:left-48 sm:top-0"
                        >
                            {hasSavedTemplates ? (
                                <div className="grid grid-cols-1 items-start justify-start gap-6 p-4 pt-12 md:grid-cols-3">
                                    {accountTemplates.map(template => (
                                        <Box key={template.id} className="relative p-4">
                                            <div className="flex flex-grow flex-col gap-4">
                                                <div className="flex flex-col gap-1">
                                                    <div className="pr-4 text-sm font-medium">
                                                        {template.name}
                                                    </div>
                                                    <div className="text-sm text-gray-600">
                                                        Created on{' '}
                                                        {isoDateFromServer(
                                                            template.created_at,
                                                        ).toLocaleString(DateTime.DATE_FULL)}
                                                    </div>
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    color="neutral"
                                                    size="sm"
                                                    className="w-full"
                                                    onClick={() =>
                                                        onTemplateSelection({
                                                            channel,
                                                            message_template_id: template.id,
                                                            subject: template.subject,
                                                            body: template.body,
                                                            body_json: template.body_json,
                                                            internal_name: template.name,
                                                            editor: template.editor,
                                                        })
                                                    }
                                                >
                                                    Use template
                                                </Button>
                                            </div>
                                            <DropdownMenu.Root>
                                                <DropdownMenu.Trigger asChild>
                                                    <Button
                                                        variant="transparent"
                                                        shape={'square'}
                                                        className="absolute right-0 top-0"
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        <FontAwesomeIcon icon={faEllipsisV} />
                                                    </Button>
                                                </DropdownMenu.Trigger>
                                                <DropdownMenu.Portal>
                                                    <DropdownMenu.Content align="end">
                                                        <DropdownMenu.Item
                                                            className="flex items-center gap-2 text-red-500"
                                                            onSelect={() =>
                                                                deleteAccountTemplate({
                                                                    templateId: template.id,
                                                                })
                                                            }
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                            Delete template
                                                        </DropdownMenu.Item>
                                                    </DropdownMenu.Content>
                                                </DropdownMenu.Portal>
                                            </DropdownMenu.Root>
                                        </Box>
                                    ))}
                                </div>
                            ) : (
                                <div
                                    className={`flex h-full flex-col items-center justify-center gap-4`}
                                >
                                    <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-gray-100 shadow-sm">
                                        <FontAwesomeIcon
                                            icon={faEnvelopeOpenText}
                                            className="text-brand-violet-primary"
                                        />
                                    </div>
                                    <p className="font-medium">No saved templates</p>
                                    <p className="text-sm">
                                        {channel === 'email' ? 'Emails' : 'Texts'} that are saved as
                                        templates will appear here.
                                    </p>
                                </div>
                            )}
                        </Tabs.Content>
                    </Tabs.Root>

                    {previewTemplateId && (
                        <div className="absolute inset-0 flex flex-col bg-white">
                            <div className="flex justify-between border-b border-gray-100 p-3">
                                <Button
                                    variant="transparent"
                                    onClick={() => setPreviewTemplateId(null)}
                                    size="sm"
                                    autoFocus
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                    Back
                                </Button>

                                <Button
                                    size="sm"
                                    variant="solid"
                                    color="primary"
                                    disabled={isFetchingAdvancedTemplate}
                                    onClick={() => {
                                        if (hasGivebutterPlusAccess) {
                                            getAdvancedTemplate({
                                                templateId: previewTemplateId,
                                            });
                                        } else {
                                            history.push(
                                                `/accounts/${accountId}/settings/about/subscription`,
                                            );
                                        }
                                    }}
                                >
                                    {isFetchingAdvancedTemplate && (
                                        <FontAwesomeIcon
                                            icon={faSpinnerThird}
                                            className="animate-spin"
                                        />
                                    )}
                                    {hasGivebutterPlusAccess ? 'Use template' : 'Upgrade to use'}
                                </Button>
                            </div>
                            <Suspense fallback={<Loader />}>
                                <EmailMessageTemplatePreview templateId={previewTemplateId} />
                            </Suspense>
                        </div>
                    )}
                    {!previewTemplateId && <Dialog.Close className="right-0 top-0 size-9" />}
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const TabsTrigger = ({ className, ...restProps }: Tabs.TabsTriggerProps) => {
    return (
        <Tabs.Trigger
            className={twMerge(
                'justify-start gap-2 rounded-lg text-sm font-medium hover:bg-white data-[state=active]:bg-white',
                className,
            )}
            {...restProps}
        />
    );
};

interface StartFromScratchCard extends BoxProps {
    channel: EngageMessageChannel;
    onSelect: () => void;
}
const StartFromScratchCard = ({
    className,
    channel,
    onSelect,
    ...restProps
}: StartFromScratchCard) => {
    return (
        <Box
            className={twMerge(
                'relative flex h-full flex-col items-center justify-center gap-4 p-4',
                className,
            )}
            {...restProps}
        >
            <div className="flex flex-col items-center gap-2 py-2">
                <div className="flex h-12 w-12 flex-col items-center justify-center rounded-lg border border-gray-100">
                    <FontAwesomeIcon
                        className="text-brand-violet-primary"
                        icon={channel === 'sms' ? faSms : faEnvelopeSolid}
                    />
                </div>
                <p className="text-center text-sm text-gray-600">
                    Create a brand new {channel === 'sms' ? 'text' : 'email'}, designed from the
                    ground up.
                </p>
            </div>
            <Button size="sm" variant="solid" color="primary" className="w-full" onClick={onSelect}>
                Start from scratch
            </Button>
        </Box>
    );
};

export { MessageTemplateDialog };
